import { Box, Text } from 'grommet';
import { capitalize } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import {
  RolePerformerType,
  TItemDefaultField as TResetField,
  TUpdateUsageEstimateItemRequest,
  UsageCycleNumber,
  UsageCycleUnit,
  UsageEstimateItem,
  UsageType,
  UsageUnitType,
} from '@talon/web/lib/api';
import { EditableText, LineItemDelete, SelectableText } from '@talon/web/src/components';
import { useEstimateStore } from '@talon/web/src/context';
import { recalcKey } from '@talon/web/src/context/stores/estimateStore';
import { TUsageEstimateItemForm } from '@talon/web/src/types';
import { USD } from '@talon/web/src/utils/currency';
import { useUndoableDelete } from '@talon/web/src/utils/useUndoableDelete';
import { getUsageInputs } from '@talon/web/src/utils/usages';
import { commercialUsageTypes, TItemDefaultField } from '@shared/types';
import { EditedMarker } from '../EditedMarker';
import { Recalculating } from '../Recalculating';

export interface IUsageEstimateDataRowProps {
  usageEstimateItem: UsageEstimateItem;
  index: number;
  useColors?: boolean;
}

export const UsageEstimateDataRow = observer(({ usageEstimateItem, index, useColors }: IUsageEstimateDataRowProps) => {
  /** Refs **/
  const defaultFormData = useRef<TUsageEstimateItemForm>({
    title: 'New Usage',
    roleId: '',
    units: '1',
    unitType: UsageUnitType.Uses,
    cycles: '1',
    rate: '0',
    taxesMultiplier: '0',
    handlingMultiplier: '0',
    pAndHMultiplier: '0',
  });

  /** Context **/
  const estimateStore = useEstimateStore();

  /** State **/
  const [isUpdating, setIsUpdating] = useState(false);
  // Hidden while its Undo toast is up; deleted for real when the toast goes
  const [hidden, setHidden] = useState(false);
  const deleteWithUndo = useUndoableDelete();

  const [title, setTitle] = useState(usageEstimateItem.title ?? defaultFormData.current.title);
  const [roleId] = useState(usageEstimateItem.role_id ?? defaultFormData.current.roleId);
  const [performerType, setRolePerformerType] = useState(
    usageEstimateItem.performer_type ?? defaultFormData.current.performerType
  );
  const [type, setType] = useState(usageEstimateItem.type ?? defaultFormData.current.type);
  const [units, setUnits] = useState(usageEstimateItem.units?.toString() ?? defaultFormData.current.units);
  const [unitType, setUnitType] = useState<UsageUnitType | undefined>(
    usageEstimateItem.unit_type ?? (defaultFormData.current.unit_type as UsageUnitType)
  );
  const [cycles, setCycles] = useState(usageEstimateItem.cycles?.toString() ?? defaultFormData.current.cycles);
  const [cycleNumber] = useState<UsageCycleNumber | null>(usageEstimateItem.cycle_number);
  const [cycleUnit, setCycleUnit] = useState<UsageCycleUnit | undefined>(
    usageEstimateItem.cycle_unit ?? (defaultFormData.current.cycleUnit as UsageCycleUnit)
  );
  const [rate, setRate] = useState(usageEstimateItem.rate?.toString() ?? defaultFormData.current.rate);
  const [subtotal, setSubtotal] = useState(usageEstimateItem.subtotal?.toString() ?? '');
  const [taxesMultiplier, setTaxesMultiplier] = useState(
    usageEstimateItem.taxes_multiplier
      ? (usageEstimateItem.taxes_multiplier * 100).toString()
      : defaultFormData.current.taxesMultiplier
  );
  const [handlingMultiplier, setHandlingMultiplier] = useState(
    usageEstimateItem.handling_multiplier
      ? (usageEstimateItem.handling_multiplier * 100).toString()
      : defaultFormData.current.handlingMultiplier
  );
  const [pAndHMultiplier, setPAndHMultiplier] = useState(
    usageEstimateItem.p_and_h_multiplier
      ? (usageEstimateItem.p_and_h_multiplier * 100).toString()
      : defaultFormData.current.pAndHMultiplier
  );
  const [subtotalPlusFringe, setSubtotalPlusFringe] = useState(
    usageEstimateItem.subtotal_plus_fringe?.toString() ?? ''
  );

  /** Effects **/

  // Repricing (a change of contract, a reset) changes these without this row saving them
  useEffect(() => {
    setRate(usageEstimateItem.rate?.toString() ?? defaultFormData.current.rate);
    setPAndHMultiplier(((usageEstimateItem.p_and_h_multiplier ?? 0) * 100).toString());
    setHandlingMultiplier(((usageEstimateItem.handling_multiplier ?? 0) * 100).toString());
    setTaxesMultiplier(((usageEstimateItem.taxes_multiplier ?? 0) * 100).toString());
  }, [
    usageEstimateItem.rate,
    usageEstimateItem.p_and_h_multiplier,
    usageEstimateItem.handling_multiplier,
    usageEstimateItem.taxes_multiplier,
  ]);

  /** Computed **/
  const canEdit = estimateStore.canEditCurrent;
  const commercialType = usageEstimateItem.usage?.commercial.type;

  // Which inputs apply, as on the Usage Estimator; an item added by hand (without a usage) shows them all
  const usageInputs = usageEstimateItem.usage?.commercial
    ? getUsageInputs(usageEstimateItem.usage, usageEstimateItem.usage.commercial)
    : undefined;
  // A cycle count other than 1 still multiplies the price, so it stays in view
  const showCycles = !usageInputs || usageInputs.showCycles || usageEstimateItem.cycles !== 1;

  // Numbers being recalculated: the whole table's (a new contract, a reset), or this row's after a save
  const rowsBusy = estimateStore.isRecalculating(recalcKey.rows('usage', usageEstimateItem.usage_estimate_id ?? ''));
  const totalsBusy = rowsBusy || estimateStore.isRecalculating(recalcKey.item(usageEstimateItem.id));
  const rateBusy = rowsBusy || estimateStore.isRecalculating(recalcKey.itemRate(usageEstimateItem.id));

  /** Methods **/
  // `changes` carries a value just picked in a select: its state update hasn't happened yet
  const updateEstimateItem = async (
    changes: Partial<
      Pick<
        TUpdateUsageEstimateItemRequest,
        | 'title'
        | 'performerType'
        | 'type'
        | 'unitType'
        | 'cycleUnit'
        | 'units'
        | 'cycles'
        | 'rate'
        | 'pAndHMultiplier'
        | 'handlingMultiplier'
        | 'taxesMultiplier'
        | 'resetToDefault'
      >
    > = {}
  ) => {
    if (isUpdating) return;

    setIsUpdating(true);

    const updatedItem = await estimateStore.updateUsageEstimateItem(usageEstimateItem.id, {
      title,
      roleId,
      type,
      performerType,
      units: parseInt(units || '1'),
      unitType: unitType as UsageUnitType,
      cycles: parseFloat(cycles || '1'),
      cycleNumber: cycleNumber as UsageCycleNumber,
      cycleUnit: cycleUnit as UsageCycleUnit,
      rate: parseFloat(rate || '0'),
      taxesMultiplier: parseFloat(taxesMultiplier || '0') / 100,
      handlingMultiplier: parseFloat(handlingMultiplier || '0') / 100,
      pAndHMultiplier: parseFloat(pAndHMultiplier || '0') / 100,
      ...changes,
    });

    if (updatedItem?.rate?.toString() !== rate) setRate(updatedItem?.rate?.toString() ?? '');
    if (updatedItem?.subtotal?.toString() !== subtotal) setSubtotal(updatedItem?.subtotal?.toString() ?? '');
    if (updatedItem?.subtotal_plus_fringe?.toString() !== subtotalPlusFringe)
      setSubtotalPlusFringe(updatedItem?.subtotal_plus_fringe?.toString() ?? '');

    const t = ((updatedItem?.taxes_multiplier || 0) * 100).toString();
    if (t !== taxesMultiplier) setTaxesMultiplier(t);

    const h = ((updatedItem?.handling_multiplier || 0) * 100).toString();
    if (h !== handlingMultiplier) setHandlingMultiplier(h);

    const p = ((updatedItem?.p_and_h_multiplier || 0) * 100).toString();
    if (p !== pAndHMultiplier) setPAndHMultiplier(p);

    setIsUpdating(false);
  };

  // Save a number just typed: its state update hasn't happened yet
  const saveTyped = (
    field: 'units' | 'cycles' | 'rate' | 'pAndHMultiplier' | 'handlingMultiplier' | 'taxesMultiplier',
    typed?: string
  ) => {
    if (typed === undefined) return updateEstimateItem();
    if (field === 'units') return updateEstimateItem({ units: parseInt(typed || '1') });
    if (field === 'cycles') return updateEstimateItem({ cycles: parseFloat(typed || '1') });
    const value = parseFloat(typed || '0');
    return updateEstimateItem({ [field]: field === 'rate' ? value : value / 100 });
  };

  // Set a value edited by hand back to the contract's
  const resetField = (field: TItemDefaultField) =>
    updateEstimateItem({ resetToDefault: [field as unknown as TResetField] });

  // The dot beside a value edited by hand
  const editedMarker = (field: TItemDefaultField) => (
    <EditedMarker item={usageEstimateItem} field={field} onReset={canEdit ? () => resetField(field) : undefined} />
  );

  const deleteEstimateItem = () =>
    deleteWithUndo({
      title: usageEstimateItem.title,
      hide: () => setHidden(true),
      restore: () => setHidden(false),
      commit: () => estimateStore.deleteUsageEstimateItem(usageEstimateItem.id),
    });

  /** Render **/
  if (hidden) return null;
  return (
    <Box>
      <Box
        className="line-item"
        direction="row"
        gap="medium"
        pad="medium"
        align="center"
        background={useColors && !(index % 2) ? 'light-1' : 'white'}
        // Numbers line up in their right-aligned columns
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <Box width="75%">
          {canEdit ? (
            <SelectableText
              val={performerType}
              options={Object.values(RolePerformerType).map((t) => ({ label: t, value: t }))}
              onChange={(value) => {
                setRolePerformerType(value as RolePerformerType);
                // Save the picked value now: the select closes on choosing, so it never blurs
                updateEstimateItem({ performerType: value as RolePerformerType });
              }}
            />
          ) : (
            <Text>{usageEstimateItem.role?.name || '-'}</Text>
          )}
        </Box>

        <Box fill="horizontal">
          {usageEstimateItem.usage_id || type ? (
            canEdit ? (
              <SelectableText
                val={type}
                options={commercialUsageTypes[commercialType]?.map((t) => ({ label: t, value: t }))}
                onChange={(value) => {
                  setType(value as UsageType);
                  // Save the picked value now: the select closes on choosing, so it never blurs
                  updateEstimateItem({ type: value as UsageType });
                }}
              />
            ) : (
              <Text>{type}</Text>
            )
          ) : canEdit ? (
            // Added by hand: no usage type, just what it's for
            <EditableText
              val={title}
              onChange={setTitle}
              onBlur={(typed?: string) => typed !== undefined && updateEstimateItem({ title: typed })}
              onSubmit={(typed?: string) => typed !== undefined && updateEstimateItem({ title: typed })}
            />
          ) : (
            <Text>{title}</Text>
          )}
        </Box>

        <Box width="75%" direction="row" gap="small" align="center" justify="end">
          <Box>
            {canEdit ? (
              <EditableText
                val={units}
                onChange={(units) => setUnits(units)}
                onBlur={(typed?: string) => saveTyped('units', typed)}
                onSubmit={(typed?: string) => saveTyped('units', typed)}
                minValue={1}
                inputType="number"
              />
            ) : (
              <Text>{units}</Text>
            )}
          </Box>
          <Box>
            {canEdit ? (
              <SelectableText
                val={unitType}
                options={Object.values(UsageUnitType).map((t) => ({ label: t, value: t }))}
                onChange={(value) => {
                  setUnitType(value as UsageUnitType);
                  // Save the picked value now: the select closes on choosing, so it never blurs
                  updateEstimateItem({ unitType: value as UsageUnitType });
                }}
              />
            ) : (
              <Text>{capitalize(unitType)}</Text>
            )}
          </Box>
        </Box>

        {/* Cycles where the usage type has them, as on the Usage Estimator */}
        <Box width="75%" direction="row" gap="small" align="center" justify="end">
          {showCycles ? (
            <>
              <Box>
                {canEdit ? (
                  <EditableText
                    val={cycles}
                    onChange={(cycles) => setCycles(cycles)}
                    onBlur={(typed?: string) => saveTyped('cycles', typed)}
                    onSubmit={(typed?: string) => saveTyped('cycles', typed)}
                    minValue={1}
                    inputType="number"
                  />
                ) : (
                  <Text>{cycles}</Text>
                )}
              </Box>
              {usageInputs ? (
                // The usage type's own cycle (e.g. "13 Week"): changing its unit here wouldn't change the price
                usageInputs.cycleLabel && <Text>× {usageInputs.cycleLabel}</Text>
              ) : (
                <Box>
                  {canEdit ? (
                    <SelectableText
                      val={cycleUnit}
                      options={Object.values(UsageCycleUnit).map((t) => ({ label: t, value: t }))}
                      onChange={(value) => {
                        setCycleUnit(value as unknown as UsageCycleUnit);
                        // Save the picked value now: the select closes on choosing, so it never blurs
                        updateEstimateItem({ cycleUnit: value as unknown as UsageCycleUnit });
                      }}
                    />
                  ) : (
                    <Text>{cycleUnit}</Text>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Text weight={300}>—</Text>
          )}
        </Box>

        <Box width="55%" direction="row" align="center" justify="end" gap="xsmall">
          {/* The dot first: the value can run past a narrow column */}
          {editedMarker('rate')}
          <Box flex={false}>
            <Recalculating busy={rateBusy}>
              {canEdit ? (
                <EditableText
                  val={rate}
                  onChange={(rate) => setRate(rate)}
                  onBlur={(typed?: string) => saveTyped('rate', typed)}
                  onSubmit={(typed?: string) => saveTyped('rate', typed)}
                  inputType="number"
                  isCurrency
                />
              ) : (
                <Text>{USD(rate)}</Text>
              )}
            </Recalculating>
          </Box>
        </Box>
        <Box width="60%" align="end">
          <Recalculating busy={totalsBusy}>
            <Text weight={600}>{USD(usageEstimateItem?.subtotal)}</Text>
          </Recalculating>
        </Box>
        <Box width="40%" direction="row" align="center" justify="end" gap="xsmall">
          {/* The dot first: the value can run past a narrow column */}
          {editedMarker('p_and_h_multiplier')}
          <Box flex={false}>
            <Recalculating busy={rowsBusy}>
              {canEdit ? (
                <EditableText
                  val={pAndHMultiplier}
                  onChange={(pAndHMultiplier) => setPAndHMultiplier(pAndHMultiplier)}
                  onBlur={(typed?: string) => saveTyped('pAndHMultiplier', typed)}
                  onSubmit={(typed?: string) => saveTyped('pAndHMultiplier', typed)}
                  inputType="number"
                  isPercent
                />
              ) : (
                <Text>{pAndHMultiplier}%</Text>
              )}
            </Recalculating>
          </Box>
        </Box>
        <Box width="40%" direction="row" align="center" justify="end" gap="xsmall">
          {/* The dot first: the value can run past a narrow column */}
          {editedMarker('handling_multiplier')}
          <Box flex={false}>
            <Recalculating busy={rowsBusy}>
              {canEdit ? (
                <EditableText
                  val={handlingMultiplier}
                  onChange={(handlingMultiplier) => setHandlingMultiplier(handlingMultiplier)}
                  onBlur={(typed?: string) => saveTyped('handlingMultiplier', typed)}
                  onSubmit={(typed?: string) => saveTyped('handlingMultiplier', typed)}
                  inputType="number"
                  isPercent
                />
              ) : (
                <Text>{handlingMultiplier}%</Text>
              )}
            </Recalculating>
          </Box>
        </Box>
        <Box width="40%" direction="row" align="center" justify="end" gap="xsmall">
          {/* The dot first: the value can run past a narrow column */}
          {editedMarker('taxes_multiplier')}
          <Box flex={false}>
            <Recalculating busy={rowsBusy}>
              {canEdit ? (
                <EditableText
                  val={taxesMultiplier}
                  onChange={(taxesMultiplier) => setTaxesMultiplier(taxesMultiplier)}
                  onBlur={(typed?: string) => saveTyped('taxesMultiplier', typed)}
                  onSubmit={(typed?: string) => saveTyped('taxesMultiplier', typed)}
                  inputType="number"
                  isPercent
                />
              ) : (
                <Text>{taxesMultiplier}%</Text>
              )}
            </Recalculating>
          </Box>
        </Box>
        <Box width="70%" align="end">
          <Recalculating busy={totalsBusy}>
            <Text weight={600}>{USD(usageEstimateItem?.subtotal_plus_fringe)}</Text>
          </Recalculating>
        </Box>
        {canEdit ? (
          <LineItemDelete name={usageEstimateItem.title} onClick={deleteEstimateItem} />
        ) : (
          <Box width="40px" flex={false} />
        )}
      </Box>
    </Box>
  );
});
